import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import Toast from '../../../components/support/Toast';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import SignupsTable from './components/SignupsTable';
import useAdminSignups from './hooks/useAdminSignups';
import { ResendAdminSignupAPI } from '../../../api/AdminAPI';

/**
 * AdminSignups - Cadastros pendentes de confirmação de e-mail (Admin Geral)
 *
 * Responsabilidades:
 * - Consome useAdminSignups() para a lista paginada/filtrada de pending_signups
 * - Renderiza AreaTitle + resumo (pendentes/expirados/confirmados/total) + tabela filtrável,
 *   com os filtros vivendo na URL para que o link possa ser compartilhado
 * - Orquestra a ação "Reenviar" (ConfirmDialog -> ResendAdminSignupAPI -> Toast + atualiza a linha)
 */
function AdminSignups() {
  const {
    signups,
    pagination,
    summary,
    isLoading,
    error,

    status,
    setStatus,
    searchInput,
    setSearchInput,
    setPage,

    updateSignup,
  } = useAdminSignups();

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [signupToResend, setSignupToResend] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleResendRequest = (signup) => {
    setSignupToResend(signup);
  };

  const handleResendCancel = () => {
    if (resendingId) return;
    setSignupToResend(null);
  };

  const handleResendConfirm = () => {
    if (!signupToResend) return;
    const { id } = signupToResend;
    setResendingId(id);
    ResendAdminSignupAPI(id, (response) => {
      setResendingId(null);
      if (!response || response.success === false) {
        showToast(response?.error || 'Erro ao reenviar o e-mail de confirmação. Tente novamente.', 'error');
        return;
      }
      setSignupToResend(null);
      updateSignup(response.signup);
      showToast('E-mail de confirmação reenviado com sucesso.', 'success');
    });
  };

  return (
    <Box sx={{ backgroundColor: '#F8F9FC', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <AreaTitle
          Title="Admin Geral · Cadastros pendentes"
          Icon={<MarkEmailUnreadOutlinedIcon />}
          Description="Cadastros que ainda não confirmaram o e-mail. O cron cleanup_pending_signups.php apaga confirmados e cadastros parados há mais de 72 h."
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Pendentes: <strong>{summary.pending}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Expirados: <strong>{summary.expired}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Confirmados: <strong>{summary.confirmed}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Total: <strong>{summary.total}</strong>
        </Typography>
      </Box>

      <SignupsTable
        signups={signups}
        isLoading={isLoading}
        error={error}
        pagination={pagination}
        onPageChange={setPage}
        onResend={handleResendRequest}
        resendingId={resendingId}
        filters={{
          status,
          onStatusChange: setStatus,
          searchInput,
          onSearchInputChange: setSearchInput,
        }}
      />

      <ConfirmDialog
        isOpen={Boolean(signupToResend)}
        title="Reenviar confirmação"
        message={signupToResend ? `Reenviar o e-mail de confirmação para ${signupToResend.email}?` : ''}
        variant="info"
        confirmLabel="Reenviar"
        cancelLabel="Cancelar"
        onConfirm={handleResendConfirm}
        onCancel={handleResendCancel}
        loading={Boolean(resendingId)}
      />

      <Toast
        open={toast.open}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        message={toast.message}
        severity={toast.severity}
        autoHideDuration={5000}
      />
    </Box>
  );
}

export default AdminSignups;
